import { z } from 'zod';

/** Сколько тегов влезает в одно поле бизнес-карточки. Первый в массиве важнее. */
export const BUSINESS_CARD_TAGS_MAX = 10;
/** Длина одного тега без решётки. */
export const BUSINESS_CARD_TAG_MAX = 40;
export const BUSINESS_CARD_TAG_SUGGESTIONS_MAX = 8;

export const BUSINESS_CARD_TAG_FIELDS = ['role', 'skills', 'industry', 'request'] as const;
export type BusinessCardTagField = (typeof BUSINESS_CARD_TAG_FIELDS)[number];

export const BUSINESS_CARD_ROLE_TAGS = [
  'бизнесмен',
  'основатель',
  'инвестор',
  'руководитель',
  'маркетолог',
  'разработчик',
  'продуктолог',
  'дизайнер',
  'продавец',
  'консультант',
  'ментор',
  'юрист',
  'финансист',
  'hr',
  'блогер',
  'трекер',
  'архитектор',
  'продакт_менеджер',
  'project_manager',
  'аналитик',
  'контент_мейкер',
] as const;

export const BUSINESS_CARD_SKILL_TAGS = [
  'вайбкодинг',
  'автоматизация',
  'ai',
  'smm',
  'видеомонтаж',
  'продажи',
  'трафик',
  'управление',
  'маркетинг',
  'финансы',
  'стратегия',
  'разработка',
  'дизайн',
  'пиар',
  'копирайтинг',
  'аналитика',
  'автоворонки',
  'crm',
  'интеграции',
  'чатботы',
  'лидогенерация',
  'юнит_экономика',
  'управление_командой',
  'кризис_менеджмент',
  'бизнес_процессы',
  'javascript',
  'python',
  'php',
  'react',
  'vue',
  'flutter',
  'docker',
  'sql',
  'nodejs',
  'laravel',
  'fastify',
  'ui_ux',
  'seo',
] as const;

export const BUSINESS_CARD_INDUSTRY_TAGS = [
  'it',
  'saas',
  'ecommerce',
  'fintech',
  'edtech',
  'crypto',
  'ai_ml',
  'маркетинг',
  'услуги',
  'horeca',
  'недвижимость',
  'производство',
  'логистика',
  'медицина',
  'игры',
  'медиа',
  'консалтинг',
  'юриспруденция',
  'строительство',
  'красота_и_здоровье',
  'биохакинг',
] as const;

export const BUSINESS_CARD_REQUEST_TAGS = [
  'ищу_партнера',
  'ищу_инвестора',
  'ищу_клиентов',
  'ищу_разработчика',
  'ищу_маркетолога',
  'ищу_команду',
  'ищу_подрядчика',
  'ищу_ментора',
  'ищу_пользователей',
  'ищу_сооснователя',
  'ищу_видеомонтажера',
  'ищу_трафик_менеджера',
] as const;

export const BUSINESS_CARD_TAG_CATALOG: Record<BusinessCardTagField, readonly string[]> = {
  role: BUSINESS_CARD_ROLE_TAGS,
  skills: BUSINESS_CARD_SKILL_TAGS,
  industry: BUSINESS_CARD_INDUSTRY_TAGS,
  request: BUSINESS_CARD_REQUEST_TAGS,
};

/** Имя колонки NetworkingProfile для поля. */
export const BUSINESS_CARD_TAG_COLUMN = {
  role: 'roleTags',
  skills: 'skillTags',
  industry: 'industryTags',
  request: 'requestTags',
} as const;

export const BusinessCardTagsDto = z.object({
  roleTags: z.array(z.string()),
  skillTags: z.array(z.string()),
  industryTags: z.array(z.string()),
  requestTags: z.array(z.string()),
});
export type BusinessCardTagsDto = z.infer<typeof BusinessCardTagsDto>;

export const EMPTY_BUSINESS_CARD_TAGS: BusinessCardTagsDto = {
  roleTags: [],
  skillTags: [],
  industryTags: [],
  requestTags: [],
};

/** Старое длинное слово роли. На карте и в каталоге его место занял «бизнесмен». */
export function aliasRoleTag(tag: string): string {
  return tag === 'предприниматель' ? 'бизнесмен' : tag;
}

/**
 * Решётка в начале не хранится, пробелы внутри токена становятся `_`.
 * Пустое и слишком длинное — null. «Предприниматель» записывается как «бизнесмен».
 */
export function normalizeBusinessCardTag(raw: string): string | null {
  const text = raw
    .trim()
    .replace(/^#+/, '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
  if (!text || text.length > BUSINESS_CARD_TAG_MAX) return null;
  return aliasRoleTag(text);
}

/** Повтор схлопывается, порядок первого вхождения сохраняется, потолок — 10. */
export function normalizeBusinessCardTags(raw: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const tag = normalizeBusinessCardTag(item);
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    out.push(tag);
    if (out.length >= BUSINESS_CARD_TAGS_MAX) break;
  }
  return out;
}

/** null — в списке есть токен, который не становится тегом (только решётки или длиннее 40). */
export function parseBusinessCardTagList(raw: readonly string[]): string[] | null {
  if (raw.some((item) => normalizeBusinessCardTag(item) === null)) return null;
  return normalizeBusinessCardTags(raw);
}

export const BusinessCardTagListInput = z.array(z.string().trim().min(1).max(80)).max(BUSINESS_CARD_TAGS_MAX);

export const BusinessCardTagsPatch = z.object({
  roleTags: BusinessCardTagListInput.optional(),
  skillTags: BusinessCardTagListInput.optional(),
  industryTags: BusinessCardTagListInput.optional(),
  requestTags: BusinessCardTagListInput.optional(),
});

export const BusinessCardTagSuggestionQuery = z.object({
  field: z.enum(BUSINESS_CARD_TAG_FIELDS),
  q: z.string().trim().min(1).max(BUSINESS_CARD_TAG_MAX),
});
export type BusinessCardTagSuggestionQuery = z.infer<typeof BusinessCardTagSuggestionQuery>;

export const BusinessCardTagSuggestionDto = z.object({
  tag: z.string(),
  count: z.number().int().nonnegative(),
});
export type BusinessCardTagSuggestionDto = z.infer<typeof BusinessCardTagSuggestionDto>;

export const BusinessCardTagSuggestionsDto = z.object({
  items: z.array(BusinessCardTagSuggestionDto).max(BUSINESS_CARD_TAG_SUGGESTIONS_MAX),
});
export type BusinessCardTagSuggestionsDto = z.infer<typeof BusinessCardTagSuggestionsDto>;

/**
 * Подсказки: живые теги из базы и совпадения каталога со счётчиком 0.
 * Сортировка — по убыванию числа анкет, не больше восьми.
 */
export function catalogTagSuggestions(
  catalog: readonly string[],
  query: string,
  used: readonly { tag: string; count: number }[],
): BusinessCardTagSuggestionDto[] {
  const needle = normalizeBusinessCardTag(query);
  if (!needle) return [];
  const counts = new Map<string, number>();
  for (const row of used) {
    const tag = normalizeBusinessCardTag(row.tag);
    if (!tag?.includes(needle)) continue;
    counts.set(tag, Math.max(counts.get(tag) ?? 0, row.count));
  }
  for (const tag of catalog) {
    if (tag.includes(needle) && !counts.has(tag)) counts.set(tag, 0);
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag, 'ru'))
    .slice(0, BUSINESS_CARD_TAG_SUGGESTIONS_MAX);
}

export function businessCardTagSuggestions(
  field: BusinessCardTagField,
  query: string,
  used: readonly { tag: string; count: number }[],
): BusinessCardTagSuggestionDto[] {
  return catalogTagSuggestions(BUSINESS_CARD_TAG_CATALOG[field], query, used);
}
